<template>
<section class="h-full w-full flex flex-wrap bg-gray-50 justify-center content-start overflow-auto">
    <div class="w-full flex flex-wrap bg-white p-4">
        <!-- Header Controls -->
        <div class="fixed top-0 w-full bg-gray-200 z-20 flex flex-wrap h-16 justify-start pl-4 content-center shadow-md">
            <!-- Title -->
            <div class="mr-4 text-lg font-bold text-gray-700 content-center flex flex-wrap">
                Reporte de Citas
            </div>

            <!-- Month/Year Display -->
            <div class="flex flex-wrap h-10 text-sm font-extrabold content-center justify-center bg-white px-4 rounded shadow-sm mr-2 border border-gray-300"
                 :class="modoRango ? 'text-purple-700 border-purple-300' : 'text-gray-700'">
                <div>{{ periodoLabel }}</div>
            </div>

            <!-- Month Controls (deshabilitados en modo rango) -->
            <button @click="prevMonth()" :disabled="modoRango"
                    class="bg-white hover:bg-gray-100 text-gray-700 font-semibold py-2 px-3 rounded shadow-sm border border-gray-300 mr-1 disabled:opacity-40 disabled:cursor-not-allowed">
                <i class="las la-chevron-left"></i>
            </button>
            <button @click="nextMonth()" :disabled="modoRango"
                    class="bg-white hover:bg-gray-100 text-gray-700 font-semibold py-2 px-3 rounded shadow-sm border border-gray-300 mr-4 disabled:opacity-40 disabled:cursor-not-allowed">
                <i class="las la-chevron-right"></i>
            </button>

            <!-- Date Range Filter -->
            <div class="flex items-center mr-4">
                <el-date-picker
                    v-model="rangoFechas"
                    type="daterange"
                    unlink-panels
                    range-separator="a"
                    start-placeholder="Fecha inicio"
                    end-placeholder="Fecha fin"
                    value-format="YYYY-MM-DD"
                    format="DD/MM/YYYY"
                    @change="onRangoChange" />
                <button v-if="modoRango" @click="clearRango()"
                        class="ml-1 bg-white hover:bg-red-50 text-red-500 font-semibold py-2 px-3 h-10 rounded shadow-sm border border-gray-300"
                        title="Quitar filtro de fechas y volver a navegación por mes">
                    <i class="las la-times"></i>
                </button>
            </div>

            <!-- Vendor Selector -->
            <div class="w-64 mr-4">
                <select v-model="vendedor_s" class="w-full h-10 px-2 rounded bg-white text-gray-700 border border-gray-300 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" @change="getData()">
                    <option :value="null">Todos los vendedores</option>
                    <option v-for="v in vendedores_lista" :key="v.id" :value="v.id">{{ v.name }}</option>
                </select>
            </div>

            <!-- Refresh Button -->
            <button class="px-3 py-2 h-10 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded shadow-sm" @click="getData()">
                <i class="las la-sync"></i> Actualizar
            </button>
        </div>

        <!-- Content Area -->
        <div class="w-full mt-16 flex flex-col items-center">
            
            <div v-if="loading" class="mt-10 text-gray-500 text-xl font-semibold">
                Cargando datos...
            </div>

            <div v-else-if="!datos || datos.length === 0" class="mt-10 text-gray-500 text-xl font-semibold">
                No hay datos para mostrar en este periodo.
            </div>

            <div v-else class="w-full max-w-7xl grid grid-cols-1 gap-6 p-4">
                
                <div v-for="vendedorGroup in datosAgrupados" :key="vendedorGroup.vendedor_id" class="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden mb-6">
                    <div class="bg-gray-100 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 class="text-lg font-bold text-gray-800 flex items-center">
                            <i class="las la-user-tie mr-2 text-blue-600 text-2xl"></i>
                            Vendedor ID: {{ vendedorGroup.vendedor_id }} 
                            <span v-if="getVendedorName(vendedorGroup.vendedor_id)" class="ml-2 text-gray-600 font-normal">
                                - {{ getVendedorName(vendedorGroup.vendedor_id) }}
                            </span>
                        </h3>
                        <span class="text-sm bg-blue-100 text-blue-800 py-1 px-3 rounded-full font-semibold">
                            {{ periodoLabel }}
                        </span>
                    </div>

                    <div class="p-6 overflow-x-auto">
                         <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Semana
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/3">
                                        Total Citas (Meta: 21)
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-1/3">
                                        Citas Antes 11 AM (Meta: 9)
                                    </th>
                                    <th scope="col" class="px-6 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                                        Bloque Eficiente
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="weekData in vendedorGroup.weeks" :key="weekData.semana" class="hover:bg-gray-50 transition-colors">
                                    <td class="px-6 py-4">
                                        <div class="text-sm font-bold text-gray-900">Semana {{ weekData.semana }}</div>
                                        <div class="text-xs text-gray-500 flex flex-col mt-1">
                                            <span>{{ weekData.fecha_inicio_semana }}</span>
                                            <span class="text-gray-400 text-[10px] leading-tight mx-auto my-0.5 hidden sm:block">|</span>
                                            <span>{{ weekData.fecha_fin_semana }}</span>
                                        </div>
                                    </td>
                                    
                                    <!-- M1: Total Citas -->
                                    <td class="px-6 py-4 cursor-pointer hover:bg-blue-50 transition-colors" @click="viewCitas('Total Citas', weekData.metricas.m1_total_citas)">
                                        <div class="flex flex-col">
                                            <div class="flex justify-between mb-1">
                                                <span class="text-sm font-medium" :class="weekData.metricas.m1_total_citas.cumple ? 'text-green-700' : 'text-gray-700'">
                                                    {{ weekData.metricas.m1_total_citas.valor }} / {{ weekData.metricas.m1_total_citas.meta }}
                                                </span>
                                                <span class="text-xs font-semibold" :class="weekData.metricas.m1_total_citas.cumple ? 'text-green-600' : 'text-orange-600'">
                                                    {{ weekData.metricas.m1_total_citas.porcentaje }}%
                                                </span>
                                            </div>
                                            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                                <div class="h-2.5 rounded-full transition-all duration-500 ease-out" 
                                                     :class="weekData.metricas.m1_total_citas.cumple ? 'bg-green-500' : 'bg-orange-400'"
                                                     :style="{ width: Math.min(weekData.metricas.m1_total_citas.porcentaje, 100) + '%' }">
                                                </div>
                                            </div>
                                            <div v-if="weekData.metricas.m1_total_citas.cumple" class="mt-1 text-xs text-green-600 flex items-center font-bold">
                                                <i class="las la-check-circle mr-1"></i> Meta Cumplida
                                            </div>
                                             <div class="mt-1 text-[10px] text-gray-400 text-right">
                                                <i class="las la-eye"></i> Ver detalle
                                            </div>
                                        </div>
                                    </td>

                                    <!-- M2: Citas Antes 11 -->
                                    <td class="px-6 py-4 cursor-pointer hover:bg-blue-50 transition-colors" @click="viewCitas('Citas Antes 11 AM', weekData.metricas.m2_citas_antes_11)">
                                        <div class="flex flex-col">
                                            <div class="flex justify-between mb-1">
                                                <span class="text-sm font-medium" :class="weekData.metricas.m2_citas_antes_11.cumple ? 'text-green-700' : 'text-gray-700'">
                                                    {{ weekData.metricas.m2_citas_antes_11.valor }} / {{ weekData.metricas.m2_citas_antes_11.meta }}
                                                </span>
                                                <span class="text-xs font-semibold" :class="weekData.metricas.m2_citas_antes_11.cumple ? 'text-green-600' : 'text-orange-600'">
                                                    {{ weekData.metricas.m2_citas_antes_11.porcentaje }}%
                                                </span>
                                            </div>
                                            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                                                <div class="h-2.5 rounded-full transition-all duration-500 ease-out" 
                                                     :class="weekData.metricas.m2_citas_antes_11.cumple ? 'bg-green-500' : 'bg-orange-400'"
                                                     :style="{ width: Math.min(weekData.metricas.m2_citas_antes_11.porcentaje, 100) + '%' }">
                                                </div>
                                            </div>
                                             <div class="mt-1 text-[10px] text-gray-400 text-right">
                                                <i class="las la-eye"></i> Ver detalle
                                            </div>
                                        </div>
                                    </td>

                                    <!-- M3: Bloque Eficiencia -->
                                    <td class="px-6 py-4 text-center cursor-pointer hover:bg-blue-50 transition-colors" @click="viewBloqueEficiencia(weekData.metricas.m3_bloque_eficiencia)">
                                        <div class="flex justify-center items-center">
                                            <div v-if="weekData.metricas.m3_bloque_eficiencia.cumple" class="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-2xl shadow-sm border border-green-200" title="Todas cumplen">
                                                <i class="las la-check"></i>
                                            </div>
                                            <div v-else class="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center text-red-500 text-xl shadow-sm border border-red-200" title="No todas cumplen">
                                                <i class="las la-times"></i>
                                            </div>
                                        </div>
                                        <div class="mt-1 text-sm font-semibold" :class="weekData.metricas.m3_bloque_eficiencia.cumple ? 'text-green-700' : 'text-gray-700'">
                                            {{ weekData.metricas.m3_bloque_eficiencia.valor }} / {{ weekData.metricas.m3_bloque_eficiencia.meta }} vendedoras
                                        </div>
                                        <div class="mt-1 text-xs text-gray-500 max-w-xs whitespace-normal">
                                            {{ weekData.metricas.m3_bloque_eficiencia.explicacion }}
                                        </div>
                                        <!-- Mini resumen por vendedora -->
                                        <div v-if="weekData.metricas.m3_bloque_eficiencia.detalle_por_vendedora" class="mt-2 flex justify-center gap-1">
                                            <span v-for="dv in weekData.metricas.m3_bloque_eficiencia.detalle_por_vendedora" :key="dv.vendedor_id"
                                                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold"
                                                  :class="dv.cumple ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'"
                                                  :title="dv.explicacion">
                                                {{ getVendedorName(dv.vendedor_id) || ('V' + dv.vendedor_id) }}
                                                <i :class="dv.cumple ? 'las la-check ml-0.5' : 'las la-times ml-0.5'"></i>
                                            </span>
                                        </div>
                                        <div class="mt-1 text-[10px] text-gray-400">
                                            <i class="las la-eye"></i> Ver detalle
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                         </table>
                    </div>
                </div>

            </div>
        </div>
    
    <!-- Modal for Bloque Eficiencia -->
    <el-dialog v-model="dialogBloqueVisible" title="Detalle Bloque Eficiente por Vendedora" width="80%" append-to-body>
        <div v-if="activeBloqueData">
            <!-- Resumen general -->
            <div class="flex items-center gap-3 mb-4 p-3 rounded-lg" :class="activeBloqueData.cumple ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'">
                <div class="h-10 w-10 rounded-full flex items-center justify-center text-xl"
                     :class="activeBloqueData.cumple ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'">
                    <i :class="activeBloqueData.cumple ? 'las la-check-circle' : 'las la-times-circle'"></i>
                </div>
                <div>
                    <div class="text-sm font-bold" :class="activeBloqueData.cumple ? 'text-green-800' : 'text-red-800'">
                        {{ activeBloqueData.valor }} de {{ activeBloqueData.meta }} vendedoras cumplen
                    </div>
                    <div class="text-xs" :class="activeBloqueData.cumple ? 'text-green-600' : 'text-red-600'">
                        {{ activeBloqueData.explicacion }}
                    </div>
                </div>
            </div>

            <!-- Detalle por vendedora -->
            <div class="space-y-3">
                <div v-for="dv in activeBloqueData.detalle_por_vendedora" :key="dv.vendedor_id"
                     class="border rounded-lg p-4"
                     :class="dv.cumple ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-2">
                            <i class="las la-user-tie text-lg" :class="dv.cumple ? 'text-green-600' : 'text-red-500'"></i>
                            <span class="font-bold text-sm text-gray-800">
                                {{ getVendedorName(dv.vendedor_id) || ('Vendedor #' + dv.vendedor_id) }}
                            </span>
                            <span class="text-xs px-2 py-0.5 rounded-full font-semibold"
                                  :class="dv.cumple ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'">
                                {{ dv.cumple ? 'Cumple' : 'No cumple' }}
                            </span>
                        </div>
                        <span class="text-xs text-gray-500">
                            {{ dv.total_citas_semana }} citas en la semana
                        </span>
                    </div>
                    <div class="text-xs text-gray-600 mb-2">{{ dv.explicacion }}</div>
                    
                    <!-- Par consecutivo si existe -->
                    <div v-if="dv.par_consecutivo" class="mt-2 bg-white rounded border border-gray-200 p-3">
                        <div class="text-xs font-semibold text-gray-700 mb-1">
                            <i class="las la-calendar-check mr-1 text-green-600"></i>
                            Par consecutivo: {{ dv.par_consecutivo.dia }} — Bloque {{ dv.par_consecutivo.bloque }}
                        </div>
                        <div v-if="dv.par_consecutivo.citas && dv.par_consecutivo.citas.length" class="mt-1 space-y-1">
                            <div v-for="(cita, idx) in dv.par_consecutivo.citas" :key="idx" class="text-[11px] text-gray-600 flex items-center gap-2 pl-2 border-l-2 border-green-300">
                                <span class="font-medium">{{ formatHora(cita.fecha_inicio) }} - {{ formatHora(cita.fecha_fin) }}</span>
                                <span v-if="cita.clientes" class="text-gray-500">{{ cita.clientes.name }} {{ cita.clientes.apellido_p }}</span>
                                <span class="text-gray-400 capitalize">{{ cita.status }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Todas las citas de la semana -->
            <div v-if="activeBloqueData.citas && activeBloqueData.citas.length" class="mt-4">
                <div class="text-sm font-bold text-gray-700 mb-2 flex items-center">
                    <i class="las la-list mr-1"></i> Todas las citas de la semana ({{ activeBloqueData.citas.length }})
                </div>
                <el-table :data="activeBloqueData.citas" style="width: 100%" max-height="300" stripe border size="small"
                          :default-sort="{ prop: 'fecha_inicio', order: 'ascending' }">
                    <el-table-column label="Fecha / Hora" width="150" sortable prop="fecha_inicio">
                        <template #default="scope">
                            <div class="text-xs font-bold text-gray-800">{{ formatFecha(scope.row.fecha_inicio) }}</div>
                            <div class="text-[11px] text-gray-500">{{ formatHora(scope.row.fecha_inicio) }} - {{ formatHora(scope.row.fecha_fin) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Vendedor" width="130">
                        <template #default="scope">
                            <span class="text-xs text-gray-700">{{ scope.row.usuario ? scope.row.usuario.name : ('ID ' + scope.row.vendedor_id) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="Cliente" min-width="160">
                        <template #default="scope">
                            <span v-if="scope.row.clientes" class="text-xs text-gray-700">{{ scope.row.clientes.name }} {{ scope.row.clientes.apellido_p }}</span>
                            <span v-else class="text-xs text-gray-400 italic">Sin cliente</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="Estatus" width="100" align="center">
                        <template #default="scope">
                            <span class="text-[11px] font-bold capitalize px-2 py-0.5 rounded"
                                  :class="{
                                      'bg-green-100 text-green-800': scope.row.status === 'finalizada',
                                      'bg-blue-100 text-blue-800': scope.row.status === 'agendada',
                                      'bg-yellow-100 text-yellow-800': scope.row.status === 'confirmada',
                                      'bg-red-100 text-red-800': scope.row.status === 'cancelada',
                                      'bg-gray-100 text-gray-800': !['finalizada','agendada','confirmada','cancelada'].includes(scope.row.status)
                                  }">
                                {{ scope.row.status }}
                            </span>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
        </div>
        <template #footer>
            <el-button @click="dialogBloqueVisible = false" type="primary">Cerrar</el-button>
        </template>
    </el-dialog>

    <!-- Modal for Citas -->
    <el-dialog v-model="dialogCitasVisible" :title="'Detalle de Citas - ' + activeMetricName" width="90%" append-to-body>
        <div v-if="activeCitas && activeCitas.length > 0">
            <!-- Resumen rápido -->
            <div class="flex flex-wrap gap-3 mb-4">
                <div class="bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-sm">
                    <span class="text-blue-600 font-bold">{{ activeCitas.length }}</span>
                    <span class="text-blue-500 ml-1">citas</span>
                </div>
                <div class="bg-green-50 border border-green-200 rounded px-3 py-1.5 text-sm">
                    <span class="text-green-600 font-bold">{{ activeCitas.filter(c => c.cotizaciones).length }}</span>
                    <span class="text-green-500 ml-1">con cotización</span>
                </div>
                <div class="bg-purple-50 border border-purple-200 rounded px-3 py-1.5 text-sm">
                    <span class="text-purple-600 font-bold">${{ formatMoney(activeCitas.reduce((sum, c) => sum + (c.cotizaciones ? parseFloat(c.cotizaciones.total) : 0), 0)) }}</span>
                    <span class="text-purple-500 ml-1">total cotizado</span>
                </div>
                <div class="bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5 text-sm">
                    <span class="text-emerald-600 font-bold">{{ activeCitas.filter(c => c.cotizaciones && c.cotizaciones.venta).length }}</span>
                    <span class="text-emerald-500 ml-1">ventas cerradas</span>
                </div>
            </div>

            <el-table :data="activeCitas" style="width: 100%" max-height="500" stripe border 
                       :default-sort="{ prop: 'fecha_inicio', order: 'ascending' }">
                
                <!-- Fecha y Hora -->
                <el-table-column label="Fecha / Hora" width="155" sortable prop="fecha_inicio">
                    <template #default="scope">
                        <div class="text-xs font-bold text-gray-800">{{ formatFecha(scope.row.fecha_inicio) }}</div>
                        <div class="text-[11px] text-gray-500">
                            {{ formatHora(scope.row.fecha_inicio) }} - {{ formatHora(scope.row.fecha_fin) }}
                        </div>
                    </template>
                </el-table-column>

                <!-- Estatus -->
                <el-table-column label="Estatus" width="110" align="center" sortable prop="status">
                    <template #default="scope">
                        <span :class="{
                            'bg-green-100 text-green-800': scope.row.status === 'finalizada',
                            'bg-blue-100 text-blue-800': scope.row.status === 'agendada',
                            'bg-yellow-100 text-yellow-800': scope.row.status === 'confirmada',
                            'bg-red-100 text-red-800': scope.row.status === 'cancelada',
                            'bg-gray-100 text-gray-800': !['finalizada', 'agendada', 'confirmada', 'cancelada'].includes(scope.row.status)
                        }" class="px-2 py-1 rounded text-[11px] font-bold capitalize">
                            {{ scope.row.status }}
                        </span>
                    </template>
                </el-table-column>

                <!-- Cliente -->
                <el-table-column label="Cliente" min-width="180">
                    <template #default="scope">
                        <div v-if="scope.row.clientes" class="leading-tight">
                            <div class="text-xs font-bold text-gray-800">
                                {{ scope.row.clientes.name }} {{ scope.row.clientes.apellido_p }} {{ scope.row.clientes.apellido_m }}
                            </div>
                            <div v-if="scope.row.clientes.phone" class="text-[11px] text-blue-600 flex items-center mt-0.5">
                                <i class="las la-phone mr-1"></i>{{ scope.row.clientes.phone }}
                            </div>
                        </div>
                        <span v-else class="text-xs text-gray-400 italic">Sin cliente</span>
                    </template>
                </el-table-column>

                <!-- Vendedor -->
                <el-table-column label="Vendedor" width="140" sortable :sort-method="(a, b) => (a.usuario?.name || '').localeCompare(b.usuario?.name || '')">
                    <template #default="scope">
                        <div v-if="scope.row.usuario" class="text-xs font-medium text-gray-700">
                            {{ scope.row.usuario.name }}
                        </div>
                        <span v-else class="text-xs text-gray-400 italic">—</span>
                    </template>
                </el-table-column>

                <!-- Observaciones -->
                <el-table-column label="Observaciones" min-width="200">
                    <template #default="scope">
                        <div v-if="scope.row.observaciones" class="text-[11px] text-gray-600 leading-snug line-clamp-3" :title="scope.row.observaciones">
                            {{ scope.row.observaciones }}
                        </div>
                        <span v-else class="text-xs text-gray-400 italic">—</span>
                    </template>
                </el-table-column>

                <!-- Dirección -->
                <el-table-column label="Dirección" min-width="200">
                    <template #default="scope">
                        <div class="text-[11px] text-gray-600 leading-snug">
                            <template v-if="scope.row.calle || scope.row.numero">
                                <div class="font-medium">{{ [scope.row.calle, scope.row.numero].filter(Boolean).join(' #') }}</div>
                            </template>
                            <template v-if="scope.row.colonia">
                                <div>{{ scope.row.colonia }}</div>
                            </template>
                            <template v-if="scope.row.municipio">
                                <div class="text-gray-500">{{ scope.row.municipio }} {{ scope.row.codigo_postal ? 'C.P. ' + scope.row.codigo_postal : '' }}</div>
                            </template>
                            <span v-if="!scope.row.calle && !scope.row.colonia && !scope.row.municipio" class="text-gray-400 italic">Sin dirección</span>
                        </div>
                    </template>
                </el-table-column>

                <!-- Cotización -->
                <el-table-column label="Cotización" width="150" align="center">
                    <template #default="scope">
                        <div v-if="scope.row.cotizaciones" class="leading-tight">
                            <div class="text-xs font-bold text-green-700">${{ formatMoney(scope.row.cotizaciones.total) }}</div>
                            <div v-if="scope.row.cotizaciones.venta" class="text-[10px] text-emerald-600 font-semibold mt-0.5">
                                <i class="las la-check-circle mr-0.5"></i>Venta: {{ scope.row.cotizaciones.venta }}
                            </div>
                            <div v-else class="text-[10px] text-orange-500 mt-0.5">
                                <i class="las la-clock mr-0.5"></i>Pendiente
                            </div>
                        </div>
                        <span v-else class="text-[11px] text-gray-400 italic">Sin cotización</span>
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <div v-else class="text-center py-10">
            <div class="text-gray-400 mb-2">
                <i class="las la-calendar-times text-4xl"></i>
            </div>
            <p class="text-gray-500 font-medium">No hay citas registradas que contribuyan a este métrico en esta semana.</p>
        </div>
         <template #footer>
            <span class="dialog-footer">
                <el-button @click="dialogCitasVisible = false" type="primary">Cerrar</el-button>
            </span>
        </template>
    </el-dialog>
</div>
</section>
</template>

<script>
import { emitter } from "../../../mitt.js";
import citas from "@/Services/citas";
import usaurios from "@/Services/user";

export default {
    name: 'ReporteCitasVentas',
    data() {
        return {
            datos: [],
            vendedores_lista: [],
            vendedor_s: null,
            loading: false,
            month: String(new Date().getMonth() + 1).padStart(2, '0'),
            year: new Date().getFullYear().toString(),
            // Filtro por rango de fechas (Opción C). null => navegación por mes.
            rangoFechas: null,
            // Modal state
            dialogCitasVisible: false,
            activeCitas: [],
            activeMetricName: '',
            // Modal bloque eficiencia
            dialogBloqueVisible: false,
            activeBloqueData: null
        }
    },
    computed: {
        viewMonthName() {
            const months = [
                "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
            ];
            return months[parseInt(this.month) - 1];
        },
        // True cuando hay un rango de fechas seleccionado.
        modoRango() {
            return Array.isArray(this.rangoFechas) && this.rangoFechas.length === 2 && this.rangoFechas[0] && this.rangoFechas[1];
        },
        // Etiqueta del periodo mostrado en el header y en las tarjetas.
        periodoLabel() {
            if (this.modoRango) {
                return `${this.formatFechaCorta(this.rangoFechas[0])} - ${this.formatFechaCorta(this.rangoFechas[1])}`;
            }
            return `${this.viewMonthName} ${this.year}`;
        },
        // Group raw data by vendedor_id
        datosAgrupados() {
            if (!this.datos) return [];
            
            // Group by vendor ID
            const grouped = this.datos.reduce((acc, curr) => {
                const vid = curr.vendedor_id;
                if (!acc[vid]) {
                    acc[vid] = {
                        vendedor_id: vid,
                        weeks: []
                    };
                }
                acc[vid].weeks.push(curr);
                return acc;
            }, {});

            // Convert to array and sort weeks
            return Object.values(grouped).map(g => {
                g.weeks.sort((a, b) => a.semana - b.semana);
                return g;
            });
        }
    },
    async mounted() {
        console.log("ReporteCitasVentas Component Mounted");
        await this.getVendedores();
        this.getData();
    },
    methods: {
        formatFecha(fechaStr) {
            if (!fechaStr) return '';
            const d = new Date(fechaStr.replace(' ', 'T'));
            const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
            const day = dias[d.getDay()];
            return `${day} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        },
        formatHora(fechaStr) {
            if (!fechaStr) return '';
            const parts = fechaStr.split(' ');
            if (parts.length < 2) return '';
            return parts[1].substring(0, 5);
        },
        formatMoney(val) {
            const num = parseFloat(val) || 0;
            return num.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        },
        viewCitas(name, metricData) {
            this.activeMetricName = name;
            this.activeCitas = metricData.citas || [];
            this.dialogCitasVisible = true;
        },
        viewBloqueEficiencia(bloqueData) {
            this.activeBloqueData = bloqueData;
            this.dialogBloqueVisible = true;
        },
        getMesAno() {
            return `${this.viewMonthName} ${this.year}`;
        },
        formatFechaCorta(fechaStr) {
            if (!fechaStr) return '';
            // fechaStr en formato YYYY-MM-DD
            const parts = fechaStr.split('-');
            if (parts.length < 3) return fechaStr;
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        },
        onRangoChange() {
            // Al elegir un rango recargamos; el modo mes queda desactivado automáticamente.
            this.getData();
        },
        clearRango() {
            this.rangoFechas = null;
            this.getData();
        },
        async getVendedores() {
            try {
                const response = await usaurios.GET({});
                if (response && !response.error) {
                    this.vendedores_lista = response.filter(v => v.role == 'vendedor');
                }
            } catch (e) {
                console.error("Error fetching vendors", e);
            }
        },
        getVendedorName(id) {
            const v = this.vendedores_lista.find(vend => vend.id === id);
            return v ? v.name : '';
        },
        prevMonth() {
            if (this.modoRango) return; // Deshabilitado mientras haya rango activo
            let m = parseInt(this.month);
            let y = parseInt(this.year);
            m--;
            if (m < 1) {
                m = 12;
                y--;
            }
            this.month = String(m).padStart(2, '0');
            this.year = String(y);
            this.getData();
        },
        nextMonth() {
            if (this.modoRango) return; // Deshabilitado mientras haya rango activo
            let m = parseInt(this.month);
            let y = parseInt(this.year);
            m++;
            if (m > 12) {
                m = 1;
                y++;
            }
            this.month = String(m).padStart(2, '0');
            this.year = String(y);
            this.getData();
        },
        async getData() {
            this.loading = true;
            this.datos = [];
            
            try {
                const params = {
                    ano: this.year,
                    mes: this.month,
                    vendedor: this.vendedor_s
                };

                // Si hay rango de fechas, se envía y el backend ignora mes/año.
                if (this.modoRango) {
                    params.fecha_desde = this.rangoFechas[0];
                    params.fecha_hasta = this.rangoFechas[1];
                }

                const response = await citas.getMetricosVendedores(params);
                
                if (response && response.data) {
                    this.datos = response.data;
                } else if (Array.isArray(response)) {
                     // Fallback if the wrapper is not standard "data" prop
                    this.datos = response;
                }
            } catch (error) {
                console.error("Error details:", error);
                emitter.emit("alerta_show", { text: "Error obteniendo reporte", type: "error" });
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
/* Optional: specific styles can be added here if tailwind isn't enough */
</style>
